/**
 * AttestationGate — the document-mode sign-off modal (GAP-P1-ATTESTATIONGATE).
 *
 * Role-configured via props: a computed readiness checklist (each failing line
 * links to its fix), forced-surface sections that MUST be read in the modal
 * (clamp verdicts, CP 04 11 text — rendered inline, never behind clicks),
 * scoped acknowledgment checkboxes (max 4, all required), a typed-name
 * signature matched (case-insensitive, trimmed) against `signerName`, a
 * react-signature-canvas ink pad, and a DM Mono digest table of exactly what
 * is being signed (+ the content hash).
 *
 * Submit stays disabled until: every checklist item is ok AND every
 * acknowledgment is checked AND the typed name matches AND (ink mode only)
 * the pad has ink. `onAttest({typed_name, acknowledged_ids, content_hash,
 * signature_method})` — the caller wires the 412
 * `acknowledged_verifier_run_id` pattern (see
 * components/accountant/review/FinalizeModal.tsx — reference only).
 *
 * ⚠️ TYPED-SIGNATURE A11Y PATH (GAP-P25-GATE-A11Y): the ink pad is a mouse/
 * touch-only surface — keyboard-only users could never complete attestation
 * before this. A visible, keyboard-reachable "Sign by typing" toggle swaps
 * the signature method between 'ink' (default, unchanged behavior) and
 * 'typed'. In 'typed' mode the ink pad is unmounted (hidden, not merely
 * disabled) and the ink requirement is dropped from the gate — the EXISTING
 * typed-name match against `signerName` is reused as the sole signature
 * proof; nothing new to type. Switching typed→ink resets `hasInk` to false
 * (the canvas remounts blank on that transition) so stale ink state can
 * never leave submit enabled — this exact class of bug (state not reset on
 * a mode/dialog transition) is why the STATE-RESET LAW below exists, and it
 * applies here too.
 *
 * ⚠️ STATE-RESET LAW (this exact defect failed two fleet builds): ALL
 * transient state — checked acknowledgments, typed name, signature-ink flag —
 * resets whenever the dialog closes BY ANY PATH, including the parent
 * toggling `open` to false directly without ever calling `onOpenChange`.
 * That is why the reset lives in a `useEffect` keyed on the `open` prop
 * (fires on the open→false transition), NOT inside the Radix onOpenChange
 * handler, which never fires for a parent-driven prop close.
 *
 * Design law: token vars ONLY (zero raw hex); StateChip is the only status
 * indicator; serif headline is document mode's signature (the only serif in a
 * modal app-wide); figures are 15px DM Mono. No new dependencies —
 * react-signature-canvas is already in package.json.
 */

import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { StateChip } from './StateChip';

// =============================================================================
// TYPES
// =============================================================================

export interface AttestationChecklistItem {
  label: string;
  ok: boolean;
  /** Link to the screen where this readiness gap gets fixed. */
  href?: string;
}

/** A section that must be READ inside the modal — rendered, never collapsed. */
export interface AttestationForcedSection {
  title: string;
  body: string;
}

export interface AttestationAcknowledgment {
  id: string;
  label: string;
}

export interface AttestationDigestRow {
  label: string;
  value: string;
}

/** How the signer proved intent to sign. */
export type SignatureMethod = 'ink' | 'typed';

export interface AttestationPayload {
  typed_name: string;
  acknowledged_ids: string[];
  content_hash: string;
  /**
   * OPTIONAL for backward compatibility (GAP-P25-GATE-A11Y): existing
   * callers/type consumers that predate the typed-signature path — e.g. code
   * that builds/destructures {typed_name, acknowledged_ids, content_hash}
   * literals — continue to compile unchanged. The runtime payload always
   * populates it going forward; treat an absent value as legacy 'ink'.
   */
  signature_method?: SignatureMethod;
}

export interface AttestationGateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Computed readiness — every item must be `ok` before the gate opens. */
  checklist: AttestationChecklistItem[];
  /** Always-visible sections (clamp verdicts, CP 04 11 text). Never hidden. */
  forcedSurface: AttestationForcedSection[];
  /** Scoped acknowledgments — max 4 rendered; every rendered one required. */
  acknowledgments: AttestationAcknowledgment[];
  /** The name the typed signature must match (case-insensitive, trimmed). */
  signerName: string;
  /** What-you-are-signing figure table — DM Mono. */
  digest: AttestationDigestRow[];
  /** Hash of the exact content being attested — displayed and echoed back. */
  contentHash: string;
  onAttest: (payload: AttestationPayload) => void;
  /** Serif headline. Defaults to "Sworn Attestation". */
  title?: string;
}

/** Contract cap: at most 4 scoped acknowledgments are ever rendered/required. */
const MAX_ACKNOWLEDGMENTS = 4;

// =============================================================================
// COMPONENT
// =============================================================================

export function AttestationGate({
  open,
  onOpenChange,
  checklist,
  forcedSurface,
  acknowledgments,
  signerName,
  digest,
  contentHash,
  onAttest,
  title = 'Sworn Attestation',
}: AttestationGateProps) {
  // Transient signing state — lives HERE (the gate stays mounted across
  // open/close cycles even though Radix unmounts the dialog content), which
  // is exactly why it must be explicitly reset on close.
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [typedName, setTypedName] = useState('');
  const [hasInk, setHasInk] = useState(false);
  const [signatureMethod, setSignatureMethod] = useState<SignatureMethod>('ink');
  const sigPadRef = useRef<SignatureCanvas | null>(null);

  // Canvas 2D strokeStyle cannot resolve `var(...)`, so resolve the ink token
  // at open time (theme-aware, and no raw hex enters this file).
  const [penColor, setPenColor] = useState<string>('');
  useEffect(() => {
    if (open && typeof window !== 'undefined') {
      const resolved = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--color-text-primary')
        .trim();
      if (resolved) setPenColor(resolved);
    }
  }, [open]);

  // ⚠️ THE RESET FIX — keyed on the `open` PROP, not on Radix onOpenChange.
  // A parent that flips `open` to false directly (no user close interaction)
  // never triggers onOpenChange; an effect on the prop catches EVERY close
  // path: escape key, overlay click, the X, Cancel, and parent prop toggles.
  useEffect(() => {
    if (!open) {
      setAcknowledgedIds(new Set());
      setTypedName('');
      setHasInk(false);
      setSignatureMethod('ink');
      // Pad is usually unmounted with the dialog content by now (ref null,
      // optional chaining absorbs it); clear defensively if it is still live.
      sigPadRef.current?.clear();
    }
  }, [open]);

  const renderedAcks = acknowledgments.slice(0, MAX_ACKNOWLEDGMENTS);

  const checklistOk = checklist.every((item) => item.ok);
  const allAcknowledged = renderedAcks.every((a) => acknowledgedIds.has(a.id));
  const trimmedName = typedName.trim();
  const nameMatches =
    trimmedName.length > 0 &&
    trimmedName.toLowerCase() === signerName.trim().toLowerCase();

  // In 'typed' mode the ink pad is not rendered and its requirement is
  // dropped — the typed-name match above is the sole signature proof.
  const canAttest =
    checklistOk &&
    allAcknowledged &&
    nameMatches &&
    (signatureMethod === 'typed' || hasInk);

  const toggleAcknowledgment = (id: string, checked: boolean) => {
    setAcknowledgedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const syncInkFromPad = () => {
    setHasInk(!(sigPadRef.current?.isEmpty() ?? true));
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setHasInk(false);
  };

  // ⚠️ STATE RESET on typed→ink (constraint (1)): the ink pad is unmounted
  // while in 'typed' mode, so switching back to 'ink' always mounts a FRESH,
  // blank SignatureCanvas. `hasInk` must be forced back to false on that
  // transition — otherwise a pad signed before an earlier typed-mode detour
  // would leave a stale `hasInk=true` that no longer reflects the (now
  // blank) canvas, and submit would wrongly stay enabled without a signature.
  const toggleSignatureMethod = () => {
    const next: SignatureMethod = signatureMethod === 'ink' ? 'typed' : 'ink';
    if (next === 'ink') {
      setHasInk(false);
      sigPadRef.current?.clear();
    }
    setSignatureMethod(next);
  };

  const handleAttest = () => {
    if (!canAttest) return;
    onAttest({
      typed_name: trimmedName,
      acknowledged_ids: renderedAcks
        .filter((a) => acknowledgedIds.has(a.id))
        .map((a) => a.id),
      content_hash: contentHash,
      signature_method: signatureMethod,
    });
  };

  const sectionLabelClass =
    'text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-dim)]';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto gap-6 bg-[var(--color-bg-surface)] border-[var(--color-border)] text-[var(--color-text-primary)]"
        data-testid="attestation-gate"
      >
        <DialogHeader>
          {/* Document mode: the only serif headline in a modal app-wide. */}
          <DialogTitle className="font-serif text-2xl font-normal tracking-tight text-[var(--color-text-primary)]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--color-text-secondary)]">
            Read everything below in full. Your signature is recorded in the
            hash-chained attestation history and binds you to the figures shown.
          </DialogDescription>
        </DialogHeader>

        {/* ------------------------------------------------------------------
            Readiness checklist — computed, each failing line links to its fix
        ------------------------------------------------------------------ */}
        <section aria-label="Readiness checklist">
          <h3 className={sectionLabelClass}>Readiness</h3>
          <ul className="mt-2 space-y-2">
            {checklist.map((item, i) => (
              <li
                key={`${item.label}-${i}`}
                className="flex items-center gap-3"
                data-testid={`attestation-checklist-item-${i}`}
              >
                <StateChip
                  state={item.ok ? 'verified' : 'needs-human'}
                  text={item.ok ? 'READY' : 'ACTION NEEDED'}
                />
                <span className="flex-1 text-sm text-[var(--color-text-secondary)]">
                  {item.label}
                </span>
                {item.href && (
                  <a
                    href={item.href}
                    className="text-sm font-medium text-[var(--color-brand)] hover:text-[var(--color-brand-hover)]"
                    data-testid={`attestation-checklist-fix-${i}`}
                  >
                    {item.ok ? 'View' : 'Fix'} →
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* ------------------------------------------------------------------
            Forced surface — clamp verdicts / CP 04 11 text. ALWAYS rendered
            in full inside the modal body; never collapsed or behind clicks.
        ------------------------------------------------------------------ */}
        {forcedSurface.length > 0 && (
          <section aria-label="Required reading">
            <h3 className={sectionLabelClass}>You are attesting with knowledge of</h3>
            <div className="mt-2 space-y-3">
              {forcedSurface.map((section, i) => (
                <div
                  key={`${section.title}-${i}`}
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
                  data-testid={`attestation-forced-section-${i}`}
                >
                  <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {section.title}
                  </h4>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-secondary)]">
                    {section.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------
            Digest — the exact figures being signed. 15px DM Mono, plus the
            content hash of the attested payload.
        ------------------------------------------------------------------ */}
        <section aria-label="Signing digest">
          <h3 className={sectionLabelClass}>What you are signing</h3>
          <table className="mt-2 w-full border-collapse" data-testid="attestation-digest">
            <tbody>
              {digest.map((row, i) => (
                <tr
                  key={`${row.label}-${i}`}
                  className="border-b border-[var(--color-border-faint)]"
                >
                  <td className="py-1.5 pr-4 text-sm text-[var(--color-text-secondary)]">
                    {row.label}
                  </td>
                  <td className="py-1.5 text-right font-mono text-[15px] tabular-nums text-[var(--color-text-primary)]">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-[var(--color-text-dim)]">
            Content hash{' '}
            <span
              className="break-all font-mono text-[var(--color-text-secondary)]"
              data-testid="attestation-content-hash"
            >
              {contentHash}
            </span>
          </p>
        </section>

        {/* ------------------------------------------------------------------
            Acknowledgments — max 4 scoped checkboxes, every one required
        ------------------------------------------------------------------ */}
        {renderedAcks.length > 0 && (
          <section aria-label="Acknowledgments">
            <h3 className={sectionLabelClass}>Acknowledgments — all required</h3>
            <div className="mt-2 space-y-2">
              {renderedAcks.map((ack) => (
                <label
                  key={ack.id}
                  className="flex cursor-pointer items-start gap-3"
                >
                  <input
                    type="checkbox"
                    checked={acknowledgedIds.has(ack.id)}
                    onChange={(e) => toggleAcknowledgment(ack.id, e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded"
                    style={{
                      borderColor: 'var(--color-border)',
                      accentColor: 'var(--color-brand)',
                    }}
                    data-testid={`attestation-ack-${ack.id}`}
                  />
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {ack.label}
                  </span>
                </label>
              ))}
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------
            Signature — typed name (must match signerName) + ink pad
        ------------------------------------------------------------------ */}
        <section aria-label="Signature">
          <div className="flex items-center justify-between">
            <h3 className={sectionLabelClass}>Signature</h3>
            {/* Keyboard-reachable a11y path (GAP-P25-GATE-A11Y): native
                <button> is focusable and Enter/Space-activatable with no
                extra wiring; aria-pressed communicates the current mode to
                assistive tech. */}
            <button
              type="button"
              aria-pressed={signatureMethod === 'typed'}
              onClick={toggleSignatureMethod}
              className="rounded-md border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] aria-pressed:border-[var(--color-brand)] aria-pressed:text-[var(--color-brand)]"
              data-testid="attestation-signature-method-toggle"
            >
              Sign by typing
            </button>
          </div>
          <div className="mt-2">
            <label
              htmlFor="attestation-typed-name"
              className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]"
            >
              Type your full name to sign
            </label>
            <input
              id="attestation-typed-name"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={signerName}
              autoComplete="off"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 font-mono text-[15px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-dim)] focus:border-[var(--color-brand)] focus:outline-none"
              data-testid="attestation-typed-name"
            />
          </div>

          {signatureMethod === 'ink' ? (
            <div className="mt-3" data-testid="attestation-signature-ink-block">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                  Draw your signature
                </span>
                <StateChip
                  state={hasInk ? 'verified' : 'dormant'}
                  text={hasInk ? 'SIGNED' : 'NOT SIGNED'}
                />
              </div>
              <div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                <SignatureCanvas
                  ref={sigPadRef}
                  {...(penColor ? { penColor } : {})}
                  onEnd={syncInkFromPad}
                  canvasProps={{
                    className: 'block w-full h-[120px]',
                    'aria-label': 'Signature pad',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="mt-1 text-xs font-medium text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]"
                data-testid="attestation-signature-clear"
              >
                Clear signature
              </button>
            </div>
          ) : (
            <p
              className="mt-3 text-xs text-[var(--color-text-dim)]"
              data-testid="attestation-signature-typed-note"
            >
              Signing by typed name — your full name above is your signature.
            </p>
          )}
        </section>

        <DialogFooter className="gap-3 border-t border-[var(--color-border-sub)] pt-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
            data-testid="attestation-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAttest}
            disabled={!canAttest}
            className="rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-semibold text-[var(--color-bg-surface)] hover:bg-[var(--color-brand-hover)] disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="attestation-submit"
          >
            Sign &amp; attest
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AttestationGate;
